import { Component, Inject, OnDestroy, OnInit, Optional, Output } from '@angular/core';
import { AngularFireAnalytics } from '@angular/fire/compat/analytics';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, Router } from '@angular/router';
import { versions } from '@assets/versions';
import { TranslateService } from '@ngx-translate/core';
import { AuthService } from '@pw-utils/services/auth/auth.service';
import { HealthCheckService } from '@pw-utils/services/health-check/health-check.service';
import { PageBlockContainerComponent } from '@shared/core/components/page-block/page-block.component';
import { PW_ENVIRONMENT } from '@shared/core/constants/injectors.constant';
import { IEnvironment } from '@shared/core/interfaces/environment.interface';
import { ForgotPasswordModalComponent } from '@utils/src/forgot-password/components/forgot-password/forgot-password.component';
import { ToastrService } from 'ngx-toastr';
import { Subject } from 'rxjs';
import { EventEmitter } from '@angular/core';


export const getCookie = (cname) => {
  let name = cname + "=";
  let ca = document.cookie.split(';');
  for (let i = 0; i < ca.length; i++) {
    let c = ca[i];
    while (c.charAt(0) == ' ') {
      c = c.substring(1);
    }
    if (c.indexOf(name) == 0) {
      return c.substring(name.length, c.length);
    }
  }
  return "";
};



// noinspection AngularMissingOrInvalidDeclarationInModule
@Component({ template: '' })
export class LoginComponent extends PageBlockContainerComponent implements OnInit, OnDestroy {
  public versions = versions;
  public loginForm: UntypedFormGroup = new UntypedFormGroup({
    username: new UntypedFormControl('', Validators.required),
    password: new UntypedFormControl('', Validators.required),
  });
  public isLoading = false;
  protected _afterLoginPath = '/main/dashboard';
  private _isLoggedIn = false;
  protected _unsubscribe$: Subject<void> = new Subject<void>();

  needsSuperUser = false;
  needsCarrierUser = false;
  needsShipperUser = false;
  needsDriverUser = false;

  forwardToNewPageAfterLogin = true;

  @Output()
  onLoginSuccess = new EventEmitter();

  constructor(
    @Optional() public health: HealthCheckService,
    protected _authService: AuthService,
    protected _router: Router,
    protected _route: ActivatedRoute,
    protected _toastr: ToastrService,
    public _dialog: MatDialog,
    public _translate: TranslateService,
    @Optional() public _angularFireAnalytics: AngularFireAnalytics,
    @Optional() @Inject(PW_ENVIRONMENT) public environment: IEnvironment
  ) {
    super();
  }

  public ngOnInit(): void {
    if (this._route.snapshot.fragment?.startsWith('/main')) {
      this._afterLoginPath = this._route.snapshot.fragment;
    }

    try {
      console.log(getCookie('pickwings_auth'));
    } catch (error) {
      console.error(error);
    }

  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next();
    this._unsubscribe$.complete();
  }

  public login(): void {
    if (this.loginForm.invalid) return;

    this.isLoading = true;
    const { username, password } = this.loginForm.value;
    this._authService
      .login(username, password)
      .subscribe((success: boolean) => {
        if (success && this.needsSuperUser) {
          if (!this._authService.hasAdminPreference()) {
            console.log('not platform admin');
            this._isLoggedIn = false;
            console.log('error');
            this._toastr.error('Error!', 'Could not login you in!');
            this._authService.logout();
          }
        }

        if (success && this.needsCarrierUser) {
          if (!this._authService.hasCarrierPreference() && !this._authService.hasAdminPreference()) {
            console.log('not carrier account');
            this._isLoggedIn = false;
            console.log('error');
            this._toastr.error(this._translate.instant('login.could_not_login_error_message'), 'Error!');
            setTimeout(() => {
              this._authService.logout();
              window.location.replace(this.environment.relateWebsite.ship);
            }, 5000);
            this.isLoading = false;
            return;
          }
        }

        if (success && this.needsShipperUser) {
          if (!this._authService.hasSenderPreference() && !this._authService.hasAdminPreference()) {
            console.log('not shipper account');
            this._isLoggedIn = false;
            console.log('error');
            this._toastr.error(this._translate.instant('login.could_not_login_error_message'), 'Error!');
            setTimeout(() => {
              this._authService.logout();
              window.location.replace(this.environment.relateWebsite.about);
            }, 5000);
            this.isLoading = false;
            return;
          }
        }

        if (success && this.needsDriverUser) {
          if (!this._authService.hasDriverUserPreference() && !this._authService.hasAdminPreference()) {
            console.log('not driver account');
            this._isLoggedIn = false;
            console.log('error');
            this._toastr.error(this._translate.instant('login.could_not_login_error_message'), 'Error!');
            setTimeout(() => {
              this._authService.logout();
              window.location.replace(this.environment.relateWebsite.about);
            }, 5000);
            this.isLoading = false;
            return;
          }
        }

        this._isLoggedIn = success;
        if (success) {
          this.onLoginSuccess.emit(null);
          if (this.forwardToNewPageAfterLogin) {
            this._router.navigateByUrl(this._afterLoginPath, { replaceUrl: true });
          } else {
            console.log('done login about not forwarding');
          }
        } else {
          console.log('error');
          this._angularFireAnalytics?.logEvent('login_failed');
          this._toastr.error('Error!', 'Could not login you in!');
        }
      })
      .add(() => (this.isLoading = false));
  }

  public forgot_password(): void {
    const { username, password } = this.loginForm.value;
    this._dialog.open(ForgotPasswordModalComponent, { data: { username } });
  }
}
